import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import Typography from "@mui/material/Typography";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SkuflixShare2Icon from "core/icons/skuflix/SkuflixShare2Icon";
import ChatMessageMenu from "core/components/ChatMessageMenu";
import { useArena } from "core/components/ArenaProvider";
import { useMutation } from "@apollo/client";
import { CreateBanMutation, DeleteChatMessageMutation } from "core/mutations";
import { withSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

function renderMessageWithMention(messageText, mentionUser) {
  if (!mentionUser) return messageText;

  const mentionName = `@${mentionUser.fullName || mentionUser.userDisplayName}`;
  const idx = messageText.indexOf(mentionName);
  if (idx === -1) return messageText;

  return (
    <>
      {messageText.slice(0, idx)}
      <Typography
        component="span"
        variant="body2"
        color="primary.main"
        sx={{ fontWeight: 500 }}
      >
        {mentionName}
      </Typography>
      {messageText.slice(idx + mentionName.length)}
    </>
  );
}

function getSystemMessageText(message, t) {
  switch (message.systemEventType) {
    case "USER_JOINED":
      return t("chat.systemMessage.userJoined", {
        name: message.systemEventData?.userDisplayName,
      });
    case "CONTENT_STARTED":
      return t("chat.systemMessage.contentStarted", {
        title: message.systemEventData?.contentTitle,
      });
    default:
      return message.message;
  }
}

const PREFIX = "ChatMessageListItem";

const classes = {
  avatar: `${PREFIX}-avatar`,
  alignItemsFlexStart: `${PREFIX}-alignItemsFlexStart`,
  listItemAvatar: `${PREFIX}-listItemAvatar`,
  userDisplayName: `${PREFIX}-userDisplayName`,
  message: `${PREFIX}-message`,
  action: `${PREFIX}-action`,
  item: `${PREFIX}-item`,
  itemText: `${PREFIX}-itemText`,
};

const StyledListItem = styled(ListItem)({
  [`& .${classes.avatar}`]: {
    margin: "auto",
    width: 24,
    height: 24,
  },
  [`& .${classes.alignItemsFlexStart}`]: {
    marginTop: 4,
  },
  [`& .${classes.listItemAvatar}`]: {
    minWidth: 40,
    paddingLeft: 12,
    paddingRight: 10,
  },
  [`& .${classes.userDisplayName}`]: {
    // fontWeight: 500,
    fontSize: 14,
    marginRight: 4,
    whiteSpace: "nowrap",
  },
  [`& .${classes.message}`]: {
    fontSize: 14,
  },
  [`& .${classes.action}`]: {
    visibility: "hidden",
  },
  [`&:hover .${classes.action}`]: {
    visibility: "visible",
  },
  [`& .${classes.itemText}`]: {
    width: "100%",
    wordBreak: "break-word",
  },
});

const PERMANENT = "PERMANENT";
const TEMPORARY = "TEMPORARY";

function ChatMessageListItem({
  message,
  viewerIsAdmin,
  viewerIsCreator,
  enqueueSnackbar,
  updateCacheAfterDelete,
  onReply,
  onMention,
}) {
  const { t } = useTranslation("core");
  const arena = useArena();

  const [chatMessageMenuAnchorEl, setChatMessageMenuAnchorEl] = useState(null);

  // Системные сообщения — отдельный рендеринг
  if (message.messageType === "SYSTEM") {
    const canMention =
      onMention && message.systemEventType === "USER_JOINED" && message.userId;

    return (
      <Box sx={{ textAlign: "center", py: 0.5, px: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={canMention ? { cursor: "pointer" } : undefined}
          onClick={
            canMention
              ? () =>
                  onMention({
                    userId: message.userId,
                    userDisplayName: message.userDisplayName,
                  })
              : undefined
          }
        >
          {getSystemMessageText(message, t)}
        </Typography>
      </Box>
    );
  }

  const onError = (error) => {
    enqueueSnackbar(error.message, {
      variant: "error",
    });
  };

  const [createBan, { loading: createBanLoading }] = useMutation(
    CreateBanMutation,
    {
      refetchQueries: ["Bans"],
      onError,
      onCompleted: (data) => {
        setChatMessageMenuAnchorEl(null);
        if (data.createBan.type === PERMANENT) {
          enqueueSnackbar(t("ban.created_permanent"), {
            variant: "success",
          });
        }
        if (data.createBan.type === TEMPORARY) {
          enqueueSnackbar(t("ban.created_temprorary"), {
            variant: "success",
          });
        }
      },
    }
  );

  const [deleteChatMessage, { loading: deleteChatMessageLoading }] =
    useMutation(DeleteChatMessageMutation, {
      variables: {
        id: message.id,
      },
      onError,
      onCompleted: () => {
        enqueueSnackbar(t("chat.message_deleted"), {
          variant: "success",
        });
      },
      update: updateCacheAfterDelete,
    });

  const onCloseChatMessageMenu = () => setChatMessageMenuAnchorEl(null);
  const onClickChatMessageMenu = (event) => {
    setChatMessageMenuAnchorEl(event.currentTarget);
  };

  const onClickBanPermanent = () => {
    createBan({
      variables: {
        input: {
          messageId: message.id,
          type: PERMANENT,
        },
      },
    });
  };

  const onClickBanTemprorary = () => {
    createBan({
      variables: {
        input: {
          messageId: message.id,
          type: TEMPORARY,
        },
      },
    });
  };

  const onClickDelete = () => {
    deleteChatMessage();
  };

  const alignItemsFlexStart = message.message.length > 30;
  const alignItems = alignItemsFlexStart ? "flex-start" : "center";
  const avatarSrc = message.userAvatar || arena?.defaultAvatar?.url;

  return (
    <>
      <StyledListItem
        alignItems={alignItems}
        dense
        disableGutters
        classes={{ container: classes.item }}
      >
        <ListItemAvatar
          classes={{
            root: classes.listItemAvatar,
            alignItemsFlexStart:
              alignItemsFlexStart && classes.alignItemsFlexStart,
          }}
        >
          <Avatar
            src={avatarSrc}
            className={classes.avatar}
            sx={onMention ? { cursor: "pointer" } : undefined}
            onClick={
              onMention
                ? () =>
                    onMention({
                      userId: message.userId,
                      userDisplayName: message.userDisplayName,
                    })
                : undefined
            }
          />
        </ListItemAvatar>
        <div className={classes.itemText}>
          {message.replyToMessage && (
            <Box
              sx={{
                borderLeft: 2,
                borderColor: "primary.main",
                pl: 1,
                pr: 6,
                mb: 0.5,
                opacity: 0.7,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {message.replyToMessage.userDisplayName}
              </Typography>
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
                noWrap
              >
                {message.replyToMessage.message}
              </Typography>
            </Box>
          )}
          <Typography
            component="span"
            variant="body2"
            color="text.secondary"
            className={classes.userDisplayName}
            sx={onMention ? { cursor: "pointer" } : undefined}
            onClick={
              onMention
                ? () =>
                    onMention({
                      userId: message.userId,
                      userDisplayName: message.userDisplayName,
                    })
                : undefined
            }
          >
            {message.userDisplayName}:
          </Typography>
          <Typography
            component="span"
            variant="body2"
            color="textPrimary"
            className={classes.message}
          >
            {renderMessageWithMention(message.message, message.mentionUser)}
          </Typography>
        </div>

        {(onReply || viewerIsAdmin || viewerIsCreator) && (
          <ListItemSecondaryAction className={classes.action}>
            {onReply && (
              <IconButton
                size="small"
                onClick={() =>
                  onReply({
                    id: message.id,
                    userId: message.userId,
                    userDisplayName: message.userDisplayName,
                    message: message.message,
                  })
                }
              >
                <SkuflixShare2Icon color="disabled" />
              </IconButton>
            )}
            {(viewerIsAdmin || viewerIsCreator) && (
              <IconButton
                edge="end"
                size="small"
                onClick={onClickChatMessageMenu}
                disabled={createBanLoading || deleteChatMessageLoading}
              >
                <MoreVertIcon color="disabled" />
              </IconButton>
            )}
          </ListItemSecondaryAction>
        )}
      </StyledListItem>
      {(viewerIsAdmin || viewerIsCreator) && (
        <ChatMessageMenu
          anchorEl={chatMessageMenuAnchorEl}
          open={Boolean(chatMessageMenuAnchorEl)}
          onClose={onCloseChatMessageMenu}
          onClickBanPermanent={onClickBanPermanent}
          onClickBanTemprorary={onClickBanTemprorary}
          onClickDelete={onClickDelete}
          viewerIsCreator={viewerIsCreator}
        />
      )}
    </>
  );
}

ChatMessageListItem.propTypes = {
  updateCacheAfterDelete: PropTypes.func.isRequired,
  enqueueSnackbar: PropTypes.func.isRequired,
  viewerIsAdmin: PropTypes.bool,
  viewerIsCreator: PropTypes.bool,
  onReply: PropTypes.func,
  onMention: PropTypes.func,
  message: PropTypes.shape({
    id: PropTypes.string.isRequired,
    messageType: PropTypes.string,
    systemEventType: PropTypes.string,
    systemEventData: PropTypes.shape({
      userId: PropTypes.string,
      userDisplayName: PropTypes.string,
    }),
    userAvatar: PropTypes.string,
    userId: PropTypes.string,
    userDisplayName: PropTypes.string,
    userIsRightholder: PropTypes.bool,
    userIsAdmin: PropTypes.bool,
    message: PropTypes.string.isRequired,
    replyToMessage: PropTypes.shape({
      userDisplayName: PropTypes.string,
      message: PropTypes.string,
    }),
    mentionUser: PropTypes.shape({
      id: PropTypes.string,
      displayName: PropTypes.string,
    }),
  }),
};

export default withSnackbar(ChatMessageListItem);
